import { useState, useEffect } from 'react';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Info,
  PlayCircle,
  PauseCircle,
  RotateCcw,
  MinusCircle,
  LogOut,
  Ban,
  Send,
  Loader,
  Loader2,
  Timer,
  GitBranch,
  RefreshCw,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { OrderEvent, VISStatusResponse } from '../../api/orders';
import { formatDate, formatDuration } from '../../utils/date';

// ── Icon & colour maps ──

const EVENT_ICONS: Record<string, typeof CheckCircle> = {
  complete: CheckCircle, completed: CheckCircle,
  create: Info, created: Info,
  process: PlayCircle, processing: PlayCircle,
  queued: Loader,
  pending: Clock,
  input_required: PauseCircle, require_action: PauseCircle,
  failed: XCircle, error: XCircle,
  canceled: Ban, cancel_submitted: Send, 'cancel.submitted': Send,
  cancel_processing: MinusCircle, canceling: MinusCircle, cancel: Ban,
  recover: RotateCcw, declined: XCircle, withdrawn: LogOut,
};

const EVENT_BG: Record<string, string> = {
  complete: 'bg-green-500', completed: 'bg-green-500',
  create: 'bg-blue-500', created: 'bg-blue-500',
  process: 'bg-amber-500', processing: 'bg-amber-500',
  queued: 'bg-indigo-500',
  pending: 'bg-amber-400',
  input_required: 'bg-orange-500', require_action: 'bg-orange-500',
  failed: 'bg-red-500', error: 'bg-red-500',
  canceled: 'bg-gray-400', cancel_submitted: 'bg-gray-400', 'cancel.submitted': 'bg-gray-400',
  cancel_processing: 'bg-gray-400', canceling: 'bg-gray-400', cancel: 'bg-gray-400',
  recover: 'bg-purple-500', declined: 'bg-rose-500', withdrawn: 'bg-slate-400',
};

function resolveIcon(type: string) {
  const t = type.toLowerCase();
  return Object.entries(EVENT_ICONS).find(([k]) => t.includes(k))?.[1] || AlertTriangle;
}
function resolveBg(type: string) {
  const t = type.toLowerCase();
  return Object.entries(EVENT_BG).find(([k]) => t.includes(k))?.[1] || 'bg-gray-400';
}

// ── VIS helpers ──

type VisState = 'done' | 'active' | 'upcoming' | 'error' | 'canceled';
type VisStage = { key: string; label: string; state: VisState };

const VIS_PIPELINE = [
  { key: 'created', label: 'Created' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'processed', label: 'Processed' },
  { key: 'pending', label: 'Pending' },
  { key: 'completed', label: 'Completed' },
];
const VIS_ORDER: Record<string, number> = {
  unknown: -1, created: 0, submitted: 1, processed: 2, pending: 3, input_required: 3,
  completed: 4, failed: 4, error: 4, canceled: 4, canceling: 3, cancelsubmitted: 2,
};
const T_ERR = ['failed', 'error'];
const T_CAN = ['canceled', 'canceling', 'cancelsubmitted'];
const T_OK = ['completed'];

function buildVisStages(raw: string): VisStage[] {
  const s = raw.toLowerCase();
  const ord = VIS_ORDER[s] ?? -1;
  return VIS_PIPELINE.map((p, i) => {
    const last = i === VIS_PIPELINE.length - 1;
    let label = p.label;
    let state: VisState = 'upcoming';
    if (s === p.key) {
      state = T_OK.includes(s) ? 'done' : T_ERR.includes(s) ? 'error' : T_CAN.includes(s) ? 'canceled' : 'active';
    } else if (last && T_ERR.includes(s)) { state = 'error'; label = s === 'error' ? 'Error' : 'Failed'; }
    else if (last && T_CAN.includes(s)) { state = 'canceled'; label = 'Canceled'; }
    else if (i < ord) { state = 'done'; }
    if (s === 'input_required' && p.key === 'pending') { state = 'active'; label = 'Input Required'; }
    return { key: p.key, label, state };
  });
}

const VIS_DOT: Record<VisState, string> = {
  done: 'bg-emerald-400 ring-emerald-400/30',
  active: 'bg-cyan-400 ring-cyan-400/30 animate-pulse',
  upcoming: 'bg-gray-300 ring-gray-300/20 dark:bg-gray-600 dark:ring-gray-600/20',
  error: 'bg-red-400 ring-red-400/30',
  canceled: 'bg-gray-400 ring-gray-400/20',
};
const VIS_LBL: Record<VisState, string> = {
  done: 'text-emerald-600 dark:text-emerald-300',
  active: 'text-cyan-600 dark:text-cyan-300 font-semibold',
  upcoming: 'text-gray-500',
  error: 'text-red-600 dark:text-red-400 font-semibold',
  canceled: 'text-gray-500 dark:text-gray-400',
};
const VIS_LINE_COLOR: Record<VisState, string> = {
  done: 'bg-emerald-400/60',
  active: 'bg-gray-300 dark:bg-gray-600',
  upcoming: 'bg-gray-200 dark:bg-gray-700',
  error: 'bg-gray-300 dark:bg-gray-600',
  canceled: 'bg-gray-300 dark:bg-gray-600',
};

// ── Branch-off index ──

function findBranchIdx(events: OrderEvent[]): number {
  const i = events.findIndex((e) => {
    const t = e.type.toLowerCase();
    return t.includes('process') || t.includes('queued');
  });
  return i >= 0 ? i : 0;
}

// ── Component ──

// ── Relative time helper ──

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}

interface Props {
  events: OrderEvent[];
  visStatus?: VISStatusResponse;
  visLoading?: boolean;
  /** Whether the order has a VIS integration (from order.vis.urn), independent of visStatus fetch */
  hasVisIntegration?: boolean;
  /** Timestamp (epoch ms) of when VIS status was last fetched */
  visLastCheckedAt?: number;
  /** Whether a VIS refetch is currently in progress */
  visRefetching?: boolean;
  /** Callback to manually trigger a VIS status refresh */
  onRefreshVis?: () => void;
}

export default function OrderBranchTimeline({ events, visStatus, visLoading, hasVisIntegration, visLastCheckedAt, visRefetching, onRefreshVis }: Props) {
  // Tick every 10s to update relative "last checked" time
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!visLastCheckedAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 10_000);
    return () => clearInterval(id);
  }, [visLastCheckedAt]);

  if ((!events || events.length === 0) && !visStatus && !hasVisIntegration) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No events recorded.</p>;
  }

  const sorted = [...(events || [])].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  // Show branch if the order has a VIS integration (even while loading)
  const showVisBranch = !!hasVisIntegration;
  const branchIdx = showVisBranch ? findBranchIdx(sorted) : -1;
  const hasStatusData = !!visStatus?.status;
  const stages = hasStatusData ? buildVisStages(visStatus!.status!) : [];

  // VIS processing duration: time between the branch-off OMS event and the next OMS event
  const visDuration = (() => {
    if (branchIdx < 0) return null;
    const branchEvent = sorted[branchIdx];
    const mergeEvent = sorted[branchIdx + 1];
    if (branchEvent && mergeEvent) return formatDuration(branchEvent.date, mergeEvent.date);
    return null;
  })();

  const NODE = 26;
  const CENTER = NODE / 2; // 13

  return (
    <div className="relative" style={{ paddingLeft: `${NODE + 14}px` }}>
      {/* Main trunk line */}
      <div
        className="absolute w-[2px] bg-gradient-to-b from-blue-500/60 via-blue-400/25 to-blue-500/60"
        style={{ left: `${CENTER - 1}px`, top: `${CENTER}px`, bottom: `${CENTER}px` }}
      />

      {sorted.map((event, idx) => {
        const Icon = resolveIcon(event.type);
        const bg = resolveBg(event.type);
        const isLast = idx === sorted.length - 1;
        const next = sorted[idx + 1];
        const dur = next ? formatDuration(event.date, next.date) : null;
        const isBranchPoint = showVisBranch && idx === branchIdx;

        return (
          <div key={`${event.type}-${event.date}-${idx}`}>
            {/* ── OMS event node ── */}
            <div className={clsx('relative flex items-center gap-3', !isLast ? 'pb-6' : '')}>
              {/* Node circle */}
              <div
                className={clsx(
                  'absolute flex items-center justify-center rounded-full ring-[3px] ring-white dark:ring-gray-900 z-10',
                  bg,
                )}
                style={{ left: `-${NODE + 14}px`, width: `${NODE}px`, height: `${NODE}px` }}
              >
                <Icon className="h-3.5 w-3.5 text-white" />
              </div>

              {/* Event content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{event.type}</span>
                  <span className="text-[11px] text-gray-500">{formatDate(event.date)}</span>
                  {dur && dur !== '-' && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-500/10 rounded-full px-2 py-0.5">
                      <Clock className="h-2.5 w-2.5" /> {dur}
                    </span>
                  )}
                </div>
                {event.message && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{event.message}</p>
                )}
              </div>
            </div>

            {/* ── VIS branch card (appears after the branch-point OMS event) ── */}
            {isBranchPoint && (
              <div className="relative pb-6 ml-0">
                {/* Diagonal connector: small line from trunk going right into the card */}
                <svg
                  className="absolute z-0 text-cyan-500/50"
                  style={{ left: `-${NODE + 14 - CENTER + 1}px`, top: '-8px', width: `${NODE + 14}px`, height: '24px' }}
                  viewBox={`0 0 ${NODE + 14} 24`}
                  fill="none"
                >
                  <path
                    d={`M ${CENTER} 0 C ${CENTER} 12, ${NODE + 14} 12, ${NODE + 14} 20`}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="4 3"
                  />
                </svg>

                {/* Branch label */}
                <div className="flex items-center gap-1.5 mb-2 mt-2">
                  <GitBranch className="h-3 w-3 text-cyan-400" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">Vendor Integration</span>
                  {visStatus?.vendor && (
                    <span className="text-[10px] text-gray-500">/ {visStatus.vendor}</span>
                  )}
                  {visDuration && visDuration !== '-' && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-cyan-700 bg-cyan-100 dark:text-cyan-300 dark:bg-cyan-500/10 rounded-full px-2 py-0.5 ml-1">
                      <Timer className="h-2.5 w-2.5" /> {visDuration}
                    </span>
                  )}
                  {visStatus?.type && (
                    <span className="ml-auto text-[10px] text-cyan-700 bg-cyan-100 dark:text-cyan-300 dark:bg-cyan-900/50 rounded px-1.5 py-0.5">{visStatus.type}</span>
                  )}
                </div>

                {/* Card */}
                <div className="rounded border border-cyan-200 bg-cyan-50 dark:border-cyan-500/20 dark:bg-cyan-950/20 p-3">
                  {visLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-1">
                      <Loader2 className="h-4 w-4 animate-spin text-cyan-500 dark:text-cyan-400" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">Loading vendor status...</span>
                    </div>
                  ) : !hasStatusData ? (
                    <p className="text-xs text-gray-500 py-1">VIS status not available</p>
                  ) : (
                    /* VIS pipeline stages */
                    <div className="flex items-center gap-0 overflow-x-auto py-0.5">
                      {stages.map((st, si) => (
                        <div key={st.key} className="flex items-center shrink-0">
                          <div className="flex flex-col items-center gap-1 min-w-[64px]">
                            <div className={clsx('w-3 h-3 rounded-full ring-[3px]', VIS_DOT[st.state])} />
                            <span className={clsx('text-[10px] leading-tight whitespace-nowrap', VIS_LBL[st.state])}>
                              {st.label}
                            </span>
                          </div>
                          {si < stages.length - 1 && (
                            <div className={clsx('h-[2px] w-5 -mt-4 shrink-0 -mx-1', VIS_LINE_COLOR[st.state])} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Last checked indicator (for non-terminal VIS statuses) */}
                  {visLastCheckedAt && visLastCheckedAt > 0 && hasStatusData && !T_OK.includes(visStatus!.status!.toLowerCase()) && !T_ERR.includes(visStatus!.status!.toLowerCase()) && !T_CAN.includes(visStatus!.status!.toLowerCase()) && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700/30 flex items-center gap-2">
                      <span className="text-[10px] text-gray-500">
                        Last checked: <span className="text-gray-600 dark:text-gray-400">{timeAgo(visLastCheckedAt)}</span>
                      </span>
                      <span className="text-[10px] text-gray-600">·</span>
                      <span className="text-[10px] text-gray-500">Auto-refreshes every 30s</span>
                      {onRefreshVis && (
                        <button
                          onClick={onRefreshVis}
                          disabled={visRefetching}
                          className="inline-flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors disabled:opacity-50 ml-auto"
                          title="Check vendor status now"
                        >
                          <RefreshCw className={clsx('h-2.5 w-2.5', visRefetching && 'animate-spin')} />
                          {visRefetching ? 'Checking...' : 'Check now'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* VIS message */}
                  {visStatus?.message && (
                    <p className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700/30 text-[11px] text-gray-500 dark:text-gray-400">
                      {visStatus.message}
                    </p>
                  )}

                  {/* VIS errors */}
                  {visStatus?.context?.errors && visStatus.context.errors.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-red-500/20 space-y-1">
                      {visStatus.context.errors.map((err, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[11px]">
                          <AlertTriangle className="h-3 w-3 text-red-400 shrink-0 mt-0.5" />
                          <span className="text-red-600 dark:text-red-300">{err.title}: {err.detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Merge connector: line going from card back to trunk */}
                <svg
                  className="absolute z-0 text-cyan-500/50"
                  style={{ left: `-${NODE + 14 - CENTER + 1}px`, bottom: '16px', width: `${NODE + 14}px`, height: '24px' }}
                  viewBox={`0 0 ${NODE + 14} 24`}
                  fill="none"
                >
                  <path
                    d={`M ${NODE + 14} 4 C ${NODE + 14} 12, ${CENTER} 12, ${CENTER} 24`}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="4 3"
                  />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
